export type BlockIdRotationError =
  | "empty"
  | "duplicate"
  | "ambiguous"
  | "not-found";

export type BlockIdRotationResult =
  | {
      ok: true;
      idMap: Map<string, string>;
      rotatedCount: number;
    }
  | {
      ok: false;
      error: BlockIdRotationError;
      duplicateIds?: string[];
      duplicateLocations?: Record<string, string[]>;
    };

interface BlockEntry {
  id: string;
  block: Record<string, any>;
  path: string;
}

function collectBlockEntries(
  nodes: unknown,
  entries: BlockEntry[] = [],
  parentPath = "content",
): BlockEntry[] {
  if (!Array.isArray(nodes)) return entries;

  for (const [index, node] of nodes.entries()) {
    if (!node || typeof node !== "object") continue;
    const block = node as Record<string, any>;
    const path = `${parentPath}[${index}]`;
    if (typeof block.id === "string" && block.id.trim()) {
      entries.push({ id: block.id, block, path });
    }
    collectBlockEntries(block.children, entries, `${path}.children`);
  }

  return entries;
}

export function findDuplicateBlockIdLocations(
  nodes: unknown,
): Record<string, string[]> {
  const locations = new Map<string, string[]>();
  for (const { id, path } of collectBlockEntries(nodes)) {
    const paths = locations.get(id) ?? [];
    paths.push(path);
    locations.set(id, paths);
  }

  return Object.fromEntries(
    [...locations.entries()].filter(([, paths]) => paths.length > 1),
  );
}

export function findDuplicateBlockIds(nodes: unknown): string[] {
  return Object.keys(findDuplicateBlockIdLocations(nodes));
}

function rewriteReference(
  value: unknown,
  idMap: ReadonlyMap<string, string>,
): unknown {
  return typeof value === "string" ? (idMap.get(value) ?? value) : value;
}

export function rewriteKnownBlockIdReferences(
  nodes: unknown,
  idMap: ReadonlyMap<string, string>,
): void {
  if (!Array.isArray(nodes)) return;

  for (const node of nodes) {
    if (!node || typeof node !== "object") continue;
    const block = node as Record<string, any>;

    if ("defaultTab" in block) {
      block.defaultTab = rewriteReference(block.defaultTab, idMap);
    }
    if ("defaultValue" in block) {
      block.defaultValue = rewriteReference(block.defaultValue, idMap);
    }

    if (Array.isArray(block.conditions)) {
      for (const condition of block.conditions) {
        if (!condition || typeof condition !== "object") continue;
        if ("block" in condition) {
          condition.block = rewriteReference(condition.block, idMap);
        }

        const source = condition.source;
        if (
          source
          && typeof source === "object"
          && (source.type === "answer" || source.type === "component-state")
        ) {
          if ("blockId" in source) {
            source.blockId = rewriteReference(source.blockId, idMap);
          }
        }
      }
    }

    rewriteKnownBlockIdReferences(block.children, idMap);
  }
}

function applyIdMap(
  entries: readonly BlockEntry[],
  idMap: ReadonlyMap<string, string>,
): void {
  for (const entry of entries) {
    const newId = idMap.get(entry.id);
    if (newId) entry.block.id = newId;
  }
}

export function rotateBlockId(
  nodes: unknown,
  oldId: string,
  newId: string,
): BlockIdRotationResult {
  const trimmed = (newId ?? "").trim();
  if (!trimmed) return { ok: false, error: "empty" };
  if (trimmed === oldId) {
    return {
      ok: true,
      idMap: new Map([[oldId, oldId]]),
      rotatedCount: 0,
    };
  }

  const entries = collectBlockEntries(nodes);
  const sourceEntries = entries.filter((entry) => entry.id === oldId);
  if (sourceEntries.length === 0) return { ok: false, error: "not-found" };
  if (sourceEntries.length > 1) {
    const duplicateLocations = findDuplicateBlockIdLocations(nodes);
    return {
      ok: false,
      error: "ambiguous",
      duplicateIds: [oldId],
      duplicateLocations: { [oldId]: duplicateLocations[oldId] ?? [] },
    };
  }
  if (entries.some((entry) => entry.id === trimmed)) {
    return { ok: false, error: "duplicate", duplicateIds: [trimmed] };
  }

  const idMap = new Map([[oldId, trimmed]]);
  applyIdMap(sourceEntries, idMap);
  rewriteKnownBlockIdReferences(nodes, idMap);

  return { ok: true, idMap, rotatedCount: 1 };
}

export function rotateAllBlockIds(
  nodes: unknown,
  generateId: () => string,
): BlockIdRotationResult {
  const entries = collectBlockEntries(nodes);
  const duplicateLocations = findDuplicateBlockIdLocations(nodes);
  const duplicateIds = Object.keys(duplicateLocations);
  if (duplicateIds.length > 0) {
    return {
      ok: false,
      error: "ambiguous",
      duplicateIds,
      duplicateLocations,
    };
  }

  const existingIds = new Set(entries.map((entry) => entry.id));
  const generatedIds = new Set<string>();
  const idMap = new Map<string, string>();

  for (const { id } of entries) {
    let newId = generateId();
    while (
      !newId
      || existingIds.has(newId)
      || generatedIds.has(newId)
    ) {
      newId = generateId();
    }
    generatedIds.add(newId);
    idMap.set(id, newId);
  }

  applyIdMap(entries, idMap);
  rewriteKnownBlockIdReferences(nodes, idMap);

  return { ok: true, idMap, rotatedCount: entries.length };
}
